import {
  differenceInCalendarDays,
  eachDayOfInterval,
  eachMonthOfInterval,
  format,
} from "date-fns";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { UsageActivity } from "@/types/usage";

import DashboardCardHeader from "@/components/DashboardCardHeader";

// 이 일수를 초과하는 기간은 일별 차트 대신 월별 차트만 표시
const MAX_DAILY_CHART_DAYS = 92;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-white border border-gray-200 p-2 rounded shadow text-sm">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} style={{ color: entry.color }} className="leading-4">
          {Number(entry.value).toLocaleString()}명
        </p>
      ))}
    </div>
  );
};

type ChartItem = {
  label: string;
  value: number;
};

type ActivityBarChartProps = {
  title: string;
  data: ChartItem[];
  isLoading: boolean;
};

function ActivityBarChart({ title, data, isLoading }: ActivityBarChartProps) {
  return (
    <DashboardCardHeader title={title} isLoading={isLoading}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
        >
          <CartesianGrid vertical={false} horizontal={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 12, fontWeight: 700, fill: "#949494", dy: 10 }}
            axisLine={{ stroke: "#655B96" }}
            tickLine={{ stroke: "#655B96" }}
          />
          <YAxis
            allowDecimals={false}
            tick={{
              fontSize: 14,
              fontWeight: 700,
              fill: "#949494",
              dx: -10,
            }}
            axisLine={{ stroke: "#655B96" }}
            tickLine={{ stroke: "#655B96" }}
          />
          <Bar
            dataKey="value"
            fill="#A76D6E"
            radius={[4, 4, 0, 0]}
            isAnimationActive={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: "#ECEEF0" }} />
        </BarChart>
      </ResponsiveContainer>
    </DashboardCardHeader>
  );
}

// API가 주지 않는 날짜/월은 0으로 채운다
const buildDailyData = (
  activity: UsageActivity | undefined,
  start: Date,
  end: Date
): ChartItem[] => {
  const countByDate = new Map(
    (activity?.daily ?? []).map((item) => [item.date, item.count])
  );
  return eachDayOfInterval({ start, end }).map((date) => ({
    label: format(date, "MM.dd"),
    value: countByDate.get(format(date, "yyyy-MM-dd")) ?? 0,
  }));
};

const buildMonthlyData = (
  activity: UsageActivity | undefined,
  start: Date,
  end: Date
): ChartItem[] => {
  const countByMonth = new Map(
    (activity?.monthly ?? []).map((item) => [item.month, item.count])
  );
  return eachMonthOfInterval({ start, end }).map((month) => ({
    label: format(month, "yy.MM"),
    value: countByMonth.get(format(month, "yyyy-MM")) ?? 0,
  }));
};

type Props = {
  activity?: UsageActivity;
  isLoading: boolean;
  startDate: string; // yyyy-MM-dd
  endDate: string; // yyyy-MM-dd
};

export default function UsageActivityCharts({
  activity,
  isLoading,
  startDate,
  endDate,
}: Props) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const showDaily =
    differenceInCalendarDays(end, start) + 1 <= MAX_DAILY_CHART_DAYS;

  return (
    <div className="flex flex-col gap-2">
      {!showDaily && (
        <div className="text-gray-B text-xs">
          기간이 길어 월별로 표시합니다.
        </div>
      )}
      <div className={showDaily ? "grid grid-cols-2 gap-5" : ""}>
        {showDaily && (
          <ActivityBarChart
            title="일별 DAU"
            data={buildDailyData(activity, start, end)}
            isLoading={isLoading}
          />
        )}
        <ActivityBarChart
          title="월별 MAU"
          data={buildMonthlyData(activity, start, end)}
          isLoading={isLoading}
        />
      </div>
      <div className="text-gray-B text-xs">
        과거 활동은 산출내역서 작업·로그인 기록 기반 근사치이며, 정확한 집계는
        2026-09-03부터 시작됩니다.
      </div>
    </div>
  );
}
